import { ReportsIcon } from '@assets/icons/minor/ReportsIcon';
import { cn } from '@components/shadcn/utils';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useSystemNotifications } from '@hooks/queries/notifications/useSystemNotifications';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Amplitude } from 'src/amplitude';
import { NotificationCenter } from '../../NotificationCenter.models';
import { BaseNotification } from '../BaseNotification';

const SYSTEM_NOTIFICATION_ICONS = {
  weekly_field_insights: ReportsIcon,
} as const;
const SYSTEM_NOTIFICATION_COLORS = {
  weekly_field_insights: '#217C74',
} as const;

function FieldInsightsReportCompleteAction({
  notification,
  notificationAction,
}: {
  readonly notification: NotificationCenter.Notification;
  readonly notificationAction: (notification: NotificationCenter.Notification) => void;
}) {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
        {t(`notifications.${notification.service}.${notification.trigger}.setup_complete`)}
      </p>
      <div className="flex gap-3 mt-2">
        <ImageryButton
          type="softPrimary"
          onClick={() => {
            Amplitude.trackNotificationsFieldInsightsChangeSettings();
            notificationAction(notification);
          }}
        >
          {t('notifications.actions.change_settings')}
        </ImageryButton>
      </div>
    </React.Fragment>
  );
}
function FieldInsightsReportSetupAction({
  notification,
  notificationAction,
}: {
  readonly notification: NotificationCenter.Notification;
  readonly notificationAction: (notification: NotificationCenter.Notification) => void;
}) {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
        {t(`notifications.${notification.service}.${notification.trigger}.setup`)}
      </p>
      <div className="flex gap-3 mt-2">
        <ImageryButton
          type="softPrimary"
          onClick={() => {
            Amplitude.trackNotificationsFieldInsightsEnable();
            notificationAction(notification);
          }}
        >
          {t('notifications.actions.enable')}
        </ImageryButton>
      </div>
    </React.Fragment>
  );
}

function FieldInsightsReportWrapperAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { data } = useSystemNotifications();
  const details = data?.[notification.id];
  const type = details?.user_notification.content.phrase_key?.split('.')[2];
  const { markAsRead, setPopoverVisible } = useNotificationsCenter();
  const [_, setParams] = useSearchParams();

  const notificationAction = useCallback(
    (notification) => {
      setParams({ subscriptionsSettings: 'multi-fields-report' });
      setPopoverVisible(false);
      markAsRead(notification);
    },
    [setParams, setPopoverVisible, markAsRead]
  );

  if (type === 'setup') {
    return (
      <FieldInsightsReportSetupAction
        notification={notification}
        notificationAction={notificationAction}
      />
    );
  } else if (type === 'setup_complete') {
    return (
      <FieldInsightsReportCompleteAction
        notification={notification}
        notificationAction={notificationAction}
      />
    );
  }

  return null;
}
const ActionComponents: {
  [key: string]: React.FC<{ readonly notification: NotificationCenter.Notification }>;
} = {
  weekly_field_insights: FieldInsightsReportWrapperAction,
} as const;

export function SystemNotification({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const ActionComponent = ActionComponents[notification.trigger];
  const Icon = SYSTEM_NOTIFICATION_ICONS[notification.trigger];
  const color = SYSTEM_NOTIFICATION_COLORS[notification.trigger];
  const iconWrapper = (
    <div
      className={cn('p-1 bg-sy-blue-10 rounded-lg', {
        ['bg-white']: notification.status === 'UNREAD',
      })}
    >
      <Icon width={24} height={24} fill={color} />
    </div>
  );
  return (
    <BaseNotification
      notification={notification}
      icon={iconWrapper}
      isSystemNotification
      ActionComponent={ActionComponent}
    />
  );
}
